import { supabase } from '../../../services/supabaseClient';
import { triggerTaskAndWait, humanizeTaskError } from './triggerApi';

const MF_TOKEN = 'QOQMRWtDQA9W8pluLNSV';
const TABLE_CORRETORES = 'bianca_realestate_corretores';
const TABLE_DOCS = 'bianca_realestate_corretor_docs';
const BUCKET = 'bianca-realestate-corretores';

// Gera uuid v4 sem dependencia extra (crypto.randomUUID em browsers modernos)
function genUuid() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

function fileExt(file) {
  const m = (file?.name || '').match(/\.([a-z0-9]+)$/i);
  return m ? m[1].toLowerCase() : 'jpg';
}

async function uploadToBucket(path, file) {
  const { error } = await supabase.storage
    .from(BUCKET)
    .upload(path, file, { upsert: true, contentType: file.type || 'image/jpeg' });
  if (error) throw error;
  const { data } = supabase.storage.from(BUCKET).getPublicUrl(path);
  return data.publicUrl;
}

// Cadastro publico do corretor. Cria linha pendente + uploads + dispara notificacao.
// docs: { creci_frente: File, foto?: File }
export async function cadastrarCorretor(formData, docs = {}) {
  const corretorId = genUuid();

  let fotoUrl = null;
  if (docs.foto) {
    fotoUrl = await uploadToBucket(`foto/${corretorId}.${fileExt(docs.foto)}`, docs.foto);
  }

  const { error: insertErr } = await supabase
    .from(TABLE_CORRETORES)
    .insert({
      id: corretorId,
      token: MF_TOKEN,
      nome: formData.nome.trim(),
      email: formData.email.trim().toLowerCase(),
      telefone: formData.telefone.trim(),
      creci: formData.creci.trim(),
      creci_uf: formData.creci_uf,
      cidade: formData.cidade.trim(),
      bio: formData.bio?.trim() || null,
      foto_url: fotoUrl,
      status: 'pendente',
    });
  if (insertErr) throw insertErr;

  // Upload dos docs sequencial pra detectar erro especifico se algum falhar
  const docTypes = ['creci_frente'];
  for (const tipo of docTypes) {
    const file = docs[tipo];
    if (!file) continue;
    const url = await uploadToBucket(`docs/${corretorId}/${tipo}.${fileExt(file)}`, file);
    await supabase.from(TABLE_DOCS).insert({
      corretor_id: corretorId,
      token: MF_TOKEN,
      tipo,
      url,
    });
  }

  // Dispara notificacao (fire-and-forget — se falhar so loga, nao bloqueia cadastro)
  try {
    await notificarCadastro({
      corretor_id: corretorId,
      nome: formData.nome,
      email: formData.email,
      telefone: formData.telefone,
      creci: formData.creci,
      cidade: formData.cidade,
    });
  } catch (err) {
    console.warn('[cadastrarCorretor] notificarCadastro falhou (nao bloqueante):', err);
  }

  return { corretor_id: corretorId };
}

export async function listCorretores({ status = null } = {}) {
  let q = supabase
    .from(TABLE_CORRETORES)
    .select('*')
    .eq('token', MF_TOKEN)
    .order('created_at', { ascending: false });
  if (status) q = q.eq('status', status);
  const { data, error } = await q;
  if (error) throw error;
  return data || [];
}

export async function getCorretorDocs(corretorId) {
  const { data, error } = await supabase
    .from(TABLE_DOCS)
    .select('*')
    .eq('corretor_id', corretorId)
    .order('created_at', { ascending: true });
  if (error) throw error;
  return data || [];
}

export async function updateComissaoSplit(corretorId, comissaoSplit) {
  const { error } = await supabase
    .from(TABLE_CORRETORES)
    .update({ comissao_split: comissaoSplit })
    .eq('id', corretorId);
  if (error) throw error;
}

async function getAccessToken() {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.access_token) throw new Error('Sessao expirada. Faca login de novo.');
  return session.access_token;
}

export async function aprovarCorretor(corretorId, comissaoSplit = 30) {
  const access_token = await getAccessToken();
  const res = await triggerTaskAndWait('mf-aprovar-corretor', {
    access_token,
    token: MF_TOKEN,
    corretor_id: corretorId,
    comissao_split: Number(comissaoSplit),
  });
  if (!res.ok) return { ok: false, error: humanizeTaskError(res.error) };
  return { ok: true, output: res.output };
}

export async function rejeitarCorretor(corretorId, motivo) {
  const access_token = await getAccessToken();
  const res = await triggerTaskAndWait('mf-rejeitar-corretor', {
    access_token,
    token: MF_TOKEN,
    corretor_id: corretorId,
    motivo,
  });
  if (!res.ok) return { ok: false, error: humanizeTaskError(res.error) };
  return { ok: true, output: res.output };
}

// Cadastro publico — chamado pela LP /seja-corretor (Passo 3)
export async function notificarCadastro({ corretor_id, nome, email, telefone, creci, cidade }) {
  const res = await triggerTaskAndWait('mf-notificar-cadastro-corretor', {
    corretor_id, nome, email, telefone, creci, cidade,
  });
  if (!res.ok) return { ok: false, error: humanizeTaskError(res.error) };
  return { ok: true };
}
